import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFormattedDate, sortPlansByCategory } from '@/helpers';
import { Button, ChecksPriceEntry, DropdownPanel, SvgIcon } from '@/components';

import { Plan } from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { paymentActions } from '@/redux/actions';
import { profileSelectors, userSelectors } from '@/redux/selectors';
import {
  ADDITION_MARK_TO_CATEGORY,
  getPlansAvailability,
  navigationPages,
  PlanCategoryEnum,
  tariffDisplayData,
} from '@/config';

import styles from './BalanceInfo.module.sass';

interface BalanceInfoProps {
  isActive: boolean;
  balance: number;
  plans: Plan[] | null;
  isUserLocked: boolean;
  isPromo?: boolean;
  onToggle: (isActive: boolean) => void;
}

const BalanceInfo: FC<BalanceInfoProps> = (props) => {
  const { isActive, balance, plans, isUserLocked, isPromo, onToggle } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [selectedEntry, setSelectedEntry] = useState<Plan | null>(null);
  const [activeCategory, setActiveCategory] = useState<PlanCategoryEnum | null>(
    null,
  );
  // Derived during render, not mirrored into state via an effect: an effect
  // lags the `plans` prop by one render, and during that render the categories
  // object was still empty while `plans` was already populated. Deriving it
  // keeps the two in sync, and sortPlansByCategory always seeds every category
  // key, so planCategories[activeCategory] is never undefined.
  const planCategories = useMemo(
    () => sortPlansByCategory(plans ?? []),
    [plans],
  );
  const { data: profileData } = useAppSelector(
    profileSelectors.getProfileSlice,
  );
  const isAiTraceOnly = useAppSelector(userSelectors.checkIfAiTraceOnly);

  useEffect(() => {
    dispatch(paymentActions.fetchPriceListPlans());
  }, []);

  useEffect(() => {
    if (profileData?.accountMode) {
      const initialCategory = getPlansAvailability(
        profileData.accountMode,
        isAiTraceOnly,
      ).available[0];
      setActiveCategory(initialCategory);
    }
  }, [profileData?.accountMode, isAiTraceOnly]);

  const planStatus = useMemo(() => {
    const currentPlan = profileData?.currentPlan;
    if (!currentPlan) return null;

    const category = ADDITION_MARK_TO_CATEGORY[currentPlan.plan_addition];
    const planName = category
      ? tariffDisplayData[category].title
      : currentPlan.plan_name;

    const meta: string[] = [];

    if (currentPlan.expires_on) {
      meta.push(
        t('translation.balance_plan_expires', {
          date: getFormattedDate(currentPlan.expires_on, 'DD.MM.YY'),
        }),
      );
    }

    const price = Number(currentPlan.plan_price);
    const credits = Number(currentPlan.check_number);
    if (price > 0 && credits > 0) {
      meta.push(
        t('translation.balance_plan_per_credit', {
          price: (price / credits).toFixed(2),
        }),
      );
    }

    return { planName, meta: meta.join(' · ') };
  }, [profileData?.currentPlan, t]);

  const hasPlansInCategory = !!(
    activeCategory && planCategories[activeCategory]?.length
  );

  const canOpenDropdown = hasPlansInCategory || !!planStatus;

  const handleOpenDropdown = useCallback(() => {
    if (isUserLocked) return;
    if (!canOpenDropdown) return;

    onToggle(true);
  }, [isUserLocked, canOpenDropdown, onToggle]);

  const goToPayment = () => {
    onToggle(false);
    navigate(navigationPages.TARIFFS.path, {
      replace: location.pathname === navigationPages.TARIFFS.path,
      state: { planId: selectedEntry?.id, step: 'payment' },
    });
  };

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        onClick={handleOpenDropdown}
        disabled={!canOpenDropdown}
        className={styles.button}
      >
        <SvgIcon name="aml" />
        <span className={styles.text}>{balance}</span>

        {isPromo && (
          <span className={styles.free}>
            | {t('translation.btn_free_check')}
          </span>
        )}
      </button>

      <DropdownPanel
        isActive={isActive}
        onClose={() => onToggle(false)}
        className={styles.panel}
      >
        <div className={styles.title}>
          {planStatus ? (
            <>
              <div className={styles.planName}>
                {t('translation.balance_plan_title', {
                  plan: planStatus.planName,
                })}
              </div>
              {planStatus.meta && (
                <div className={styles.planMeta}>{planStatus.meta}</div>
              )}
            </>
          ) : (
            t('translation.buy_credits_title')
          )}
        </div>

        {activeCategory && hasPlansInCategory && (
          <div className={styles.list}>
            {planCategories[activeCategory].map((entry) => {
              return (
                <ChecksPriceEntry
                  key={entry.id}
                  isActive={entry === selectedEntry}
                  item={entry}
                  onClick={() => setSelectedEntry(entry)}
                />
              );
            })}
          </div>
        )}

        {hasPlansInCategory && (
          <Button
            size="m"
            disabled={!selectedEntry}
            onClick={goToPayment}
            fullwidth
          >
            {t('translation.continue_to_payment_btn')}
          </Button>
        )}

        <button
          type="button"
          className={styles.close}
          onClick={() => onToggle(false)}
        >
          <SvgIcon name="close" />
        </button>
      </DropdownPanel>
    </div>
  );
};

export default BalanceInfo;
